import { useLocation } from "react-router-dom";
import { useAuth } from "../../../../providers/AuthProvider";
import { useAppStore } from "../../../../providers/AppStore";

/**
 * Which History page is this, and whose records does it show?
 *
 * The same screen serves all three shells, and the shell decides both the scope
 * of the data and which tabs exist:
 *
 * - `company` — the affiliate main tab (`/client/history-list`, AffiliateShell).
 *   Every client of the company, so the listings carry a Client column and a
 *   client filter.
 * - `client` — one client, in the workspace tab (`/client/history`,
 *   WorkspaceShell mode="client"). The subject is the `?id=` client.
 * - `self` — the logged-in customer (`/platform/history`, WorkspaceShell
 *   mode="self").
 *
 * `client` and `self` have a Timeline tab; `company` does not.
 *
 * Resolved from the pathname rather than from context, because the shells
 * differ in what they even provide: AffiliateShell has no `SubjectProvider`, so
 * a `useSubject()` call there would throw.
 */
export type HistoryScopeKind = "company" | "client" | "self";

export interface HistoryScope {
  kind: HistoryScopeKind;
  /** Route prefix for tab and detail links, e.g. `/client/history-list`. */
  basePath: string;
  /** The one person whose records to list. Null in `company` scope. */
  subjectId: string | null;
  /** True when every listing is about one person — `client` or `self`. */
  isSingleSubject: boolean;
  /**
   * The chart plots one person's biomarkers over time, so it needs a subject —
   * present in the client workspace and the customer's own workspace, absent
   * from the cross-client view (the design's client-picker variant there is
   * not built).
   */
  showTimeline: boolean;
}

export const HISTORY_BASE_PATHS: Record<HistoryScopeKind, string> = {
  company: "/client/history-list",
  client: "/client/history",
  self: "/platform/history",
};

const useHistoryScope = (): HistoryScope => {
  const location = useLocation();
  const { AuthUser } = useAuth();
  const { ASSelectedClient } = useAppStore();

  // `/client/history-list` first: it is not a prefix of `/client/history`, but
  // testing it first keeps the intent obvious.
  const kind: HistoryScopeKind = location.pathname.startsWith(
    HISTORY_BASE_PATHS.company
  )
    ? "company"
    : location.pathname.startsWith(HISTORY_BASE_PATHS.self)
    ? "self"
    : "client";

  const subjectId =
    kind === "self"
      ? AuthUser?.pnoe_user_id ?? null
      : kind === "client"
      ? ASSelectedClient?.id ?? null
      : null;

  return {
    kind,
    basePath: HISTORY_BASE_PATHS[kind],
    subjectId,
    isSingleSubject: kind !== "company",
    showTimeline: kind !== "company",
  };
};

export default useHistoryScope;
